import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
  Button,
  ButtonGroup,
  Dialog,
  Slide,
  Toolbar,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { Close } from '@mui/icons-material';
import DialogContent from '@mui/material/DialogContent';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { employeeEntity, ticketTagEntity } from 'shared/entityDetails';
import { TextEditor, withStaticSelectEditor } from 'shared/components/data-display/editors';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import { AsyncMultipleSelect } from 'shared/components/select/SearchableSelects';
import { VARIANTS } from 'shared/constants';
import { TooltipIconButton } from 'shared/components/abstract/interactables';
import ContactInformation from './subcomponents/ContactInformation';
import TicketResolver from './subcomponents/TicketResolver';
import CollapsibleEntryTable from './subcomponents/CollapsibleEntryTable';
import { NewEntryDialog } from './subcomponents/EntryDialog';
import MobileEntryContainer from './subcomponents/MobileEntryContainer';
import { TICKET_PRIORITY, TICKET_STATUS } from '../../shared/constants/support';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const ticketInformationLeft = [
  {
    label: 'Title',
    key: 'title',
    editor: TextEditor,
  },
  {
    label: 'Assignee',
    key: 'assignee_id',
    editor: withAsyncSelect({
      ...employeeEntity,
      label: 'Assignee',
    }),
    editKey: 'assignee_id',
  },
];

const ticketInformationCenter = [
  {
    label: 'Priority',
    key: 'priority',
    editor: withStaticSelectEditor(Object.values(TICKET_PRIORITY)),
  },
  {
    label: 'Status',
    key: 'status',
    editor: withStaticSelectEditor(Object.values(TICKET_STATUS)
      .filter((status) => status !== 'All')),
  },
];

const createdBy = [
  {
    label: 'Created By',
    jsonKey: 'created_by',
    formatter: (ticket) => (ticket.created_by),
    editInline: false,
    readOnly: true,
  },
];

/**
 * @param {Object} props
 * @param {Object} props.data
 * @param {Object} [props.updatedData={ readOnly: true }]
 * @param {Function} props.handleChange
 * @param {Function} props.handleReload
 * @param {boolean} props.showResolutionTypes
 * @param {Function} props.setStagedTicketTags
 * @param {Array<*>} props.stagedTicketTags
 * @param {Function} props.validateTagLength
 * @param {boolean} props.disableOptions
 * @param {boolean} props.isManager
 */
export default function TicketInfo(props) {
  const {
    handleChange,
    data,
    updatedData = { readOnly: true },
    showResolutionTypes,
    handleReload,
    stagedTicketTags,
    setStagedTicketTags,
    validateTagLength,
    disableOptions,
    isManager,
  } = props;

  const isMobile = useMediaQuery('(max-width: 600px)');
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(!isMobile);
  const showDialog = () => setShow(true);
  const hideDialog = () => {
    setShow(false);
  };

  function expandCollapseIcon() {
    return (
      open
        ? (
          <TooltipIconButton
            className="entry-icon"
            message="Collapse text"
            icon={ExpandLessIcon}
            onClick={() => setOpen(!open)}
          />
        )
        : (
          <TooltipIconButton
            className="entry-icon"
            message="Expand text"
            icon={ExpandMoreIcon}
            onClick={() => setOpen(!open)}
          />
        )
    );
  }

  return (
    <Box sx={{ padding: '10px', backgroundColor: 'subtleBackground', borderRadius: '.25rem' }}>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        {isMobile ? (
          <Grid container size={12} rowSpacing={1} columnSpacing={2}>
            <Grid
              size={{ xs: 12, lg: 6 }}
              sx={{ width: '100%' }}
            >
              <div className="item-child-title">
                Ticket Information
                {expandCollapseIcon()}
              </div>
              <Collapse in={open} timeout="auto">
                <DataFormatter
                  editing
                  data={data}
                  descriptions={ticketInformationLeft}
                  updatedData={updatedData}
                  updateValue={handleChange}
                />
                <AsyncMultipleSelect
                  searchRequest={ticketTagEntity.searchRequest}
                  getOptionLabel={ticketTagEntity.getOptionLabel}
                  onChange={setStagedTicketTags}
                  label="Tag"
                  selectedOptions={stagedTicketTags}
                  onBlur={validateTagLength}
                  getOptionDisabled={() => disableOptions}
                />
                <DataFormatter
                  editing
                  data={data}
                  descriptions={ticketInformationCenter}
                  updatedData={updatedData}
                  updateValue={handleChange}
                />
                <TicketResolver
                  data={data}
                  display={(updatedData?.status !== 'Active' && (data?.status === 'Resolved' || updatedData?.status === 'Resolved'))
                    && showResolutionTypes}
                  handleChange={handleChange}
                  updatedData={updatedData}
                />
                <DataFormatter
                  data={data}
                  descriptions={createdBy}
                  updatedData={updatedData}
                  updateValue={handleChange}
                />
              </Collapse>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            size={{ xs: 12, lg: 8 }}
            rowSpacing={1}
            columnSpacing={2}
          >
            <Grid size={12}>
              <div className="item-child-title">
                Ticket Information
                {expandCollapseIcon()}
              </div>
              <Collapse in={open} timeout="auto">
                <Grid container size={12} rowSpacing={1} columnSpacing={2}>
                  <Grid size={{ lg: 6, xs: 12 }}>
                    <DataFormatter
                      editing
                      data={data}
                      descriptions={ticketInformationLeft}
                      updatedData={updatedData}
                      updateValue={handleChange}
                    />
                    <AsyncMultipleSelect
                      searchRequest={ticketTagEntity.searchRequest}
                      getOptionLabel={ticketTagEntity.getOptionLabel}
                      onChange={setStagedTicketTags}
                      label="Tag"
                      selectedOptions={stagedTicketTags}
                      onBlur={validateTagLength}
                      getOptionDisabled={() => disableOptions}
                    />
                  </Grid>
                  <Grid size={{ lg: 6, xs: 12 }}>
                    <DataFormatter
                      editing
                      data={data}
                      descriptions={ticketInformationCenter}
                      updatedData={updatedData}
                      updateValue={handleChange}
                    />
                    <TicketResolver
                      data={data}
                      display={(updatedData?.status !== 'Active' && (data?.status === 'Resolved' || updatedData?.status === 'Resolved'))
                        && showResolutionTypes}
                      handleChange={handleChange}
                      updatedData={updatedData}
                    />
                    <DataFormatter
                      data={data}
                      descriptions={createdBy}
                      updatedData={updatedData}
                      updateValue={handleChange}
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        )}
        <Grid size={{ xs: 12, lg: 4 }}>
          <ContactInformation
            data={data.ticket_contact || undefined}
            updatedData={updatedData}
            handleChange={handleChange}
            mediaQuery={isMobile}
          />
        </Grid>
        <Grid
          container
          size={12}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Grid size={{ lg: 6, xs: 12 }}>
            <div className="item-child-title">
              Ticket Entries
              {
                !isMobile
                && (
                  <TooltipIconButton
                    className="clear-text borderless-icon-btn"
                    message="Expand the entries table"
                    icon={AspectRatioIcon}
                    onClick={showDialog}
                  />
                )
              }
            </div>
          </Grid>
          <Grid size={{ lg: 6, xs: 12 }}>
            <NewEntryDialog
              ticketId={data.id}
              reload={handleReload}
              isMobile={isMobile}
            />
          </Grid>
          <Dialog
            open={show}
            onClose={hideDialog}
            fullScreen
            fullWidth
            slots={{
              transition: Transition,
            }}
          >
            <Toolbar>
              <Typography
                style={{
                  wordBreak: 'break-word',
                  paddingRight: '10px',
                }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
                component="div"
              >
                {`Ticket: ${data.id} - ${data.title}`}
              </Typography>
              <ButtonGroup>
                <Button
                  edge="start"
                  variant={VARIANTS.outlined}
                  onClick={hideDialog}
                  aria-label="close"
                >
                  <Close />
                </Button>
              </ButtonGroup>
            </Toolbar>
            <DialogContent>
              {show
                && (
                  <CollapsibleEntryTable
                    entries={data.entries}
                    className="history-items-wrapper"
                    reload={handleReload}
                    assigneeId={data?.assignee_id}
                    isManager={isManager}
                    inDialog
                  />
                )}
            </DialogContent>
          </Dialog>
          {
            isMobile
              ? (
                <div className="card-box-buffer">
                  <MobileEntryContainer
                    id={data.id}
                    entries={data.entries}
                    className="table-height-large"
                    reload={handleReload}
                    emptyMessage="No Ticket Entries"
                  />
                </div>
              )
              : (
                <CollapsibleEntryTable
                  entries={data.entries}
                  className="table-height-sm"
                  reload={handleReload}
                  assigneeId={data?.assignee_id}
                  isManager={isManager}
                />
              )
          }
        </Grid>
      </Grid>
    </Box>
  );
}
